import {
  Grid,
  FormControl,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { touchpointsMocks } from 'pages/calculator/MediaPlanning/components/Touchpoints/mocks';
import { ReactComponent as CheckboxCheckedFilledIcon } from 'assets/icons/checkbox-checked-filled.svg';
import { useParams } from 'react-router-dom';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import usePlanStore from '../../../../../../../store/planStore';
import ErrorMessage from '../../../../common/ErrorMessage/ErrorMessage';
import { META_CONVERTS_BRANDS } from './types';
import { useAuthContext } from '../../../../../../../context/AuthContext';
import linksConfig from '../../../../../../../config/linking';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';
import MultiSelectAutocomplete from '../../../../../../../shared/components/MultiSelectAutocomplete/MultiSelectAutocomplete';

interface PartnerSelectorsProps {
  partners: { id: string; title: string }[];
  partnerBudgetShareConversion: number;
  partnerBudgetShareConsideration: number;
  fetchedPartnersData?: { partners: string[] };
  handleChangeStateValues: (
    key: string,
    value: number | string | boolean | { id: string; title: string }
  ) => void;
  metaConverts?: boolean;
}

function PartnersSelectors({
  partners,
  partnerBudgetShareConversion,
  partnerBudgetShareConsideration,
  metaConverts,
  fetchedPartnersData,
  handleChangeStateValues,
}: PartnerSelectorsProps) {
  const { t } = useTranslation();

  const { id } = useParams();
  const { alreadySavedPlanId } = usePlanStore.getState();
  const { userRoles } = useAuthContext();

  const { countryForms, selectedCountry, savedGeneralInfoState } =
    usePlanStore();

  const { metaConfig } = countryForms[selectedCountry!];

  const isPartnersError =
    metaConfig?.error && metaConfig?.partners?.length === 0;

  if (metaConfig?.isGoldenRuleMet === false) {
    return null;
  }

  return (
    <Grid container>
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <FormControl fullWidth>
          <MultiSelectAutocomplete
            options={fetchedPartnersData?.partners?.map((partner) => ({
              id: partner,
              title: partner,
            }))}
            id="partnersMultiSelect"
            label={t('mediaPlanning.partners')}
            noOptionsText={t('mediaPlanning.noPartners')}
            onChange={(_: any, value: string) =>
              handleChangeStateValues('partners', value)
            }
            value={partners}
            limitTags={1}
            disableClearable={false}
            error={isPartnersError}
          />
          {isPartnersError && (
            <ErrorMessage message={t('mediaPlanning.errors.partners')} />
          )}
        </FormControl>
      </Grid>
      <Grid
        item
        xs={3}
        sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="caption" lineHeight={1}>
          {`${t(
            'mediaPlanning.partnerBudgetShareConsideration'
          )} (${convertDecimalToPercentValue(
            partnerBudgetShareConsideration ?? 0
          )}%)`}
        </Typography>
        <Slider
          valueLabelDisplay="auto"
          value={partnerBudgetShareConsideration}
          step={0.05}
          min={touchpointsMocks.metaSliders.budgetShareConsideration.min}
          max={touchpointsMocks.metaSliders.budgetShareConsideration.max}
          marks={[
            {
              value: touchpointsMocks.metaSliders.budgetShareConsideration.min,
              label: convertDecimalToPercentValue(
                touchpointsMocks.metaSliders.budgetShareConsideration.min
              ),
            },
            {
              value: touchpointsMocks.metaSliders.budgetShareConsideration.max,
              label: convertDecimalToPercentValue(
                touchpointsMocks.metaSliders.budgetShareConsideration.max
              ),
            },
          ]}
          valueLabelFormat={(value) =>
            `${convertDecimalToPercentValue(value).toFixed(0)}%`
          }
          onChange={(_, newValue) =>
            handleChangeStateValues(
              'partnerBudgetShareConsideration',
              +newValue
            )
          }
        />
      </Grid>
      <Grid
        item
        xs={3}
        sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="caption" lineHeight={1}>
          {`${t(
            'mediaPlanning.partnerBudgetShareConversion'
          )} (${convertDecimalToPercentValue(
            partnerBudgetShareConversion ?? 0
          )}%)`}
        </Typography>
        <Slider
          valueLabelDisplay="auto"
          value={partnerBudgetShareConversion}
          step={0.05}
          min={touchpointsMocks.metaSliders.budgetShareConversion.min}
          max={touchpointsMocks.metaSliders.budgetShareConversion.max}
          marks={[
            {
              value: touchpointsMocks.metaSliders.budgetShareConversion.min,
              label: convertDecimalToPercentValue(
                touchpointsMocks.metaSliders.budgetShareConversion.min
              ),
            },
            {
              value: touchpointsMocks.metaSliders.budgetShareConversion.max,
              label: convertDecimalToPercentValue(
                touchpointsMocks.metaSliders.budgetShareConversion.max
              ),
            },
          ]}
          valueLabelFormat={(value) =>
            `${convertDecimalToPercentValue(value).toFixed(0)}%`
          }
          onChange={(_, newValue) =>
            handleChangeStateValues('partnerBudgetShareConversion', +newValue)
          }
        />
      </Grid>
      <Grid
        item
        xs={3}
        sx={{ pr: '24px', display: 'flex', alignItems: 'center' }}
      >
        {savedGeneralInfoState?.brand &&
          savedGeneralInfoState.brand in META_CONVERTS_BRANDS && (
            <FormControlLabel
              color="gray.main"
              value="end"
              control={
                <Checkbox
                  icon={<CheckboxCheckedFilledIcon />}
                  checked={metaConverts}
                  onChange={(_, checked) =>
                    handleChangeStateValues('metaConverts', checked)
                  }
                />
              }
              label={
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="gray.silver"
                >
                  {t('mediaPlanning.metaConverts')}
                </Typography>
              }
              labelPlacement="end"
            />
          )}
        <HelperTooltip
          tooltipData={linksConfig.partnerShareRecommendation}
          iconAdjust="-42px"
        />
      </Grid>
    </Grid>
  );
}

export default PartnersSelectors;
